import { Card } from "react-bootstrap";
import StyledDl from "./StyledDl";

interface InrixDataSetsProps {
  boundary?: string;
}

const InrixDataSets = ({ boundary }: InrixDataSetsProps) => {
  return (
    <>
      <Card className="mb-2">
        <Card.Header>Traffic Volume Data</Card.Header>
        <div className="d-flex flex-row align-items-start">
          <Card.Img
            src="/roadnetwork.svg"
            style={{ width: 80 }}
            className="m-3"
          />
          <Card.Body>
            <Card.Text as="div">
              <p>
                Inrix provides high-resolution INRIX traffic volume data that
                enables organisations to understand how traffic flows across the
                road network throughout the week. The dataset combines estimated
                Average Daily Traffic (ADT) values with detailed time-of-week
                profiles, allowing users to analyse traffic demand at both
                strategic and operational levels.
              </p>
              <p>
                Each road segment includes an estimated ADT along with a weekly
                traffic profile at 15-minute intervals. This provides 672 time
                periods per segment (96 intervals per day across seven days),
                enabling detailed analysis of how traffic varies by time of day
                and day of week.
              </p>
              <p>
                The data supports a wide range of applications including
                transport modelling, traffic impact assessments, infrastructure
                planning, site selection, and mobility analysis. By providing
                both overall traffic volumes and detailed temporal patterns, the
                dataset allows users to identify peak periods, understand
                weekday and weekend traffic behaviour, and evaluate how traffic
                demand changes over time.
              </p>

              <h4 className="dataset-section-header mt-3">Key Features</h4>
              <StyledDl>
                <dt>Network-wide traffic coverage</dt>
                <dd>
                  Traffic volume estimates are provided across the road network,
                  allowing users to analyse traffic demand beyond locations
                  where traditional traffic counters exist.
                </dd>
                <dt>High temporal resolution</dt>
                <dd>
                  Traffic profiles are available at up to 15-minute intervals,
                  providing detailed insight into daily and weekly traffic
                  patterns.
                </dd>
                <dt>Average Daily Traffic estimates</dt>
                <dd>
                  Each road segment includes an estimated ADT value representing
                  typical daily traffic volumes.
                </dd>
                <dt>Time-of-week traffic profiles</dt>
                <dd>
                  A full weekly traffic distribution allows analysis of peak
                  periods, weekday versus weekend behaviour, and hourly traffic
                  variation.
                </dd>
                <dt>Easy integration with GIS and analytics platforms</dt>
                <dd>
                  The dataset can be delivered in formats suitable for GIS,
                  database, and analytical workflows.
                </dd>
              </StyledDl>

              <h4 className="dataset-section-header mt-3">Typical Use Cases</h4>
              <StyledDl>
                <dt>Transport modelling and planning</dt>
                <dd>
                  Support transport planning studies by providing detailed
                  traffic demand information across the network.
                </dd>
                <dt>Traffic impact assessments</dt>
                <dd>
                  Understand existing traffic levels and peak period demand for
                  development planning and infrastructure assessments.
                </dd>
                <dt>Site selection and location analysis</dt>
                <dd>
                  Assess traffic exposure and accessibility when evaluating
                  potential retail, logistics, or commercial locations.
                </dd>
                <dt>Infrastructure and highway planning</dt>
                <dd>
                  Support road network analysis, corridor studies, and
                  infrastructure investment decisions.
                </dd>
                <dt>Mobility and accessibility analysis</dt>
                <dd>
                  Combine traffic volumes with travel time and accessibility
                  datasets to better understand transport demand and movement
                  patterns.
                </dd>
              </StyledDl>

              <h4 className="dataset-section-header mt-3">
                Data Specification
              </h4>
              <StyledDl>
                <dt>Road segment identifier</dt>
                <dd>Unique identifier representing the road segment.</dd>
                <dt>Average Daily Traffic (ADT)</dt>
                <dd>
                  Estimated number of vehicles travelling on the segment on a
                  typical day.
                </dd>
                <dt>Time-of-week traffic profile</dt>
                <dd>
                  672 traffic volume values representing estimated vehicle
                  counts for each 15-minute interval across a full week (96
                  intervals per day × 7 days).
                </dd>
                <dt>Temporal coverage</dt>
                <dd>
                  Full weekly distribution from Monday 00:00 to Sunday 24:00.
                </dd>
                <dt>Data delivery formats</dt>
                <dd>Parquet and CSV.</dd>
              </StyledDl>

              <a
                href={
                  boundary ?
                    `/boundaries/${boundary}/Traffic%20Volume%20Data`
                  : `/datasets/Traffic%20Volume%20Data`
                }
                className="btn btn-primary"
                role="button"
              >
                Get Traffic Volume Data
              </a>
            </Card.Text>
          </Card.Body>
        </div>
      </Card>
      <Card className="mb-2">
        <Card.Header>Speed Profile Data</Card.Header>
        <div className="d-flex flex-row align-items-start">
          <Card.Img
            src="/routelines.svg"
            style={{ width: 80 }}
            className="m-3"
          />
          <Card.Body>
            <Card.Text as="div">
              <p>
                Inrix provides detailed road speed profile data that enables
                organisations to understand how traffic speeds vary across the
                road network throughout the day and week. The dataset is derived
                from large-scale connected vehicle observations and provides
                insight into real-world driving behaviour across urban,
                suburban, and rural roads.
              </p>
              <p>
                Speed profile data provides a statistical view of how traffic
                typically moves along each road segment, allowing users to
                analyse congestion patterns, identify periods of slower traffic,
                and understand how speeds vary by time of day and day of week.
                These profiles help transportation professionals move beyond
                simple averages to understand how traffic conditions change
                throughout the week.
              </p>
              <p>
                By combining detailed speed patterns with road network data, the
                dataset supports transport planning, infrastructure investment
                decisions, traffic modelling, and mobility analysis.
              </p>

              <h4 className="dataset-section-header mt-3">Key Features</h4>
              <StyledDl>
                <dt>Network-wide coverage</dt>
                <dd>
                  Speed profiles are available across a wide range of road
                  types, enabling consistent analysis across entire transport
                  networks rather than only at fixed monitoring locations.
                </dd>
                <dt>Time-based speed profiles</dt>
                <dd>
                  Typical traffic speeds are provided for each time period of
                  the week, allowing analysis of daily and weekly congestion
                  patterns.
                </dd>
                <dt>Historical traffic speed insights</dt>
                <dd>
                  Historical speed observations provide a reliable view of how
                  traffic typically behaves across different times of day.
                </dd>
                <dt>Speed distribution analysis</dt>
                <dd>
                  Speed profiles can include statistical speed distributions,
                  allowing users to understand the full range of observed speeds
                  rather than relying solely on averages.
                </dd>
                <dt>Integration with GIS and analytics tools</dt>
                <dd>
                  The dataset can be integrated into GIS, dashboards, transport
                  models, and data analytics workflows.
                </dd>
              </StyledDl>

              <h4 className="dataset-section-header mt-3">Typical Use Cases</h4>
              <StyledDl>
                <dt>Transport planning and congestion analysis</dt>
                <dd>
                  Understand how traffic speeds vary across the network to
                  support infrastructure planning and congestion management.
                </dd>
                <dt>Road safety and speed analysis</dt>
                <dd>
                  Analyse the distribution of vehicle speeds to identify
                  locations where excessive speeds may contribute to safety
                  risks.
                </dd>
                <dt>Travel time and network performance analysis</dt>
                <dd>
                  Combine speed profiles with network distances to calculate
                  travel times and understand network reliability.
                </dd>
                <dt>Signal optimisation and corridor management</dt>
                <dd>
                  Identify where traffic speeds are affected by signal timing,
                  congestion, or network constraints.
                </dd>
                <dt>Location and accessibility analysis</dt>
                <dd>
                  Evaluate how traffic conditions affect travel times and
                  accessibility to key destinations.
                </dd>
              </StyledDl>

              <h4 className="dataset-section-header mt-3">
                Data Specification
              </h4>
              <StyledDl>
                <dt>Road segment identifier</dt>
                <dd>Unique identifier representing the road segment.</dd>
                <dt>Average traffic speed</dt>
                <dd>Typical observed traffic speed for each road segment.</dd>
                <dt>Time-of-week speed profile</dt>
                <dd>
                  Speed values provided by time of day and day of week to
                  describe how traffic speeds change throughout the week.
                </dd>
                <dt>Speed distribution metrics</dt>
                <dd>
                  Statistical measures such as percentile speeds or speed ranges
                  that help describe the full distribution of observed speeds.
                </dd>
                <dt>Temporal coverage</dt>
                <dd>
                  Profiles represent typical traffic behaviour across different
                  times of the week.
                </dd>
                <dt>Data delivery formats</dt>
                <dd>CSV.</dd>
              </StyledDl>

              <a
                href={
                  boundary ?
                    `/boundaries/${boundary}/Speed%20Profile%20Data`
                  : `/datasets/Speed%20Profile%20Data`
                }
                className="btn btn-primary"
                role="button"
              >
                Get Speed Profile Data
              </a>
            </Card.Text>
          </Card.Body>
        </div>
      </Card>
      <Card className="mb-2">
        <Card.Header>Trips Data</Card.Header>
        <div className="d-flex flex-row align-items-start">
          <Card.Img
            src="/pt-network.svg"
            style={{ width: 80 }}
            className="m-3"
          />
          <Card.Body>
            <Card.Text as="div">
              <p>
                Inrix provides detailed trip movement data that reveals how
                vehicles travel across the road network. The dataset captures
                origin, destination, route, distance and travel time
                characteristics for millions of anonymised vehicle trips,
                allowing organisations to understand real-world travel behaviour
                and mobility patterns.
              </p>
              <p>
                Trips data provides insight into how people move between
                locations, enabling analysis of travel demand, route choice,
                trip lengths, and journey patterns across cities, regions and
                countries. By analysing aggregated trip movements, organisations
                can better understand where journeys begin and end, how people
                travel through the network, and how travel patterns vary over
                time.
              </p>
              <p>
                This dataset supports a wide range of transport planning,
                infrastructure planning, economic development, and mobility
                analysis applications.
              </p>

              <h4 className="dataset-section-header mt-3">Key Features</h4>
              <StyledDl>
                <dt>Origin–destination trip data</dt>
                <dd>
                  Trips are represented with origin and destination locations,
                  allowing analysis of travel demand between zones or locations.
                </dd>
                <dt>Route and travel characteristics</dt>
                <dd>
                  Trips include route distance, travel time, and other
                  attributes that describe how journeys are made across the
                  network.
                </dd>
                <dt>Large-scale trip observations</dt>
                <dd>
                  The dataset is derived from large volumes of anonymised
                  vehicle movements, providing broad coverage of travel
                  behaviour across road networks.
                </dd>
                <dt>Temporal analysis of travel demand</dt>
                <dd>
                  Trip data can be analysed by time of day, day of week, or
                  other time periods to understand how travel demand changes
                  over time.
                </dd>
                <dt>Aggregation and spatial analysis</dt>
                <dd>
                  Trips can be aggregated to zones, corridors, or regions to
                  support strategic planning and mobility studies.
                </dd>
              </StyledDl>

              <h4 className="dataset-section-header mt-3">Typical Use Cases</h4>
              <StyledDl>
                <dt>Origin–destination analysis</dt>
                <dd>
                  Understand where trips begin and end in order to analyse
                  travel demand between areas.
                </dd>
                <dt>Transport planning and modelling</dt>
                <dd>
                  Support demand modelling and network planning by analysing
                  real-world travel patterns.
                </dd>
                <dt>Infrastructure planning and investment</dt>
                <dd>
                  Identify key travel corridors and high-demand routes to
                  support infrastructure investment decisions.
                </dd>
                <dt>Economic and land use analysis</dt>
                <dd>
                  Understand how travel demand relates to employment centres,
                  residential areas, and commercial activity.
                </dd>
                <dt>Mobility and travel behaviour analysis</dt>
                <dd>
                  Study travel patterns to understand commuting flows, regional
                  travel patterns, and changes in mobility behaviour.
                </dd>
              </StyledDl>

              <h4 className="dataset-section-header mt-3">
                Data Specification
              </h4>
              <StyledDl>
                <dt>Trip identifier</dt>
                <dd>Unique identifier representing each trip record.</dd>
                <dt>Origin location</dt>
                <dd>
                  Starting point of the trip, typically represented by
                  coordinates or spatial zones.
                </dd>
                <dt>Destination location</dt>
                <dd>End point of the trip.</dd>
                <dt>Trip distance</dt>
                <dd>Estimated distance travelled during the trip.</dd>
                <dt>Travel time</dt>
                <dd>Estimated duration of the journey.</dd>
                <dt>Route characteristics</dt>
                <dd>
                  Information describing how the trip travelled across the road
                  network.
                </dd>
                <dt>Temporal attributes</dt>
                <dd>
                  Trip start and/or end time allowing analysis of travel
                  behaviour over time.
                </dd>
                <dt>Data delivery formats</dt>
                <dd>CSV</dd>
              </StyledDl>

              <a
                href={
                  boundary ?
                    `/boundaries/${boundary}/Trips%20Data`
                  : `/datasets/Trips%20Data`
                }
                className="btn btn-primary"
                role="button"
              >
                Get Trips Data
              </a>
            </Card.Text>
          </Card.Body>
        </div>
      </Card>
    </>
  );
};

export default InrixDataSets;
